import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
    FileCode, Hash, Activity, Info, RefreshCw
} from 'lucide-react'

type TabType = 'checks' | 'sri' | 'docs'
type CheckStatus = 'pass' | 'warning' | 'fail'

interface IntegrityCheck {
    name: string
    desc: string
    status: CheckStatus
    hash: string
    file: string
}

const CHECKS: IntegrityCheck[] = [
    { name: 'vendor_core.ts', desc: 'Núcleo DRM — hash SHA-384 verificado', status: 'pass', hash: 'sha384-oqVuA...f3Kp', file: 'src/vendor_core.ts' },
    { name: 'supabase.ts', desc: 'Cliente Supabase — integridad OK', status: 'pass', hash: 'sha384-mBzX1...k9Wr', file: 'src/lib/supabase.ts' },
    { name: 'App.tsx', desc: 'Componente raíz — hash coincide', status: 'pass', hash: 'sha384-pLmN2...h7Qz', file: 'src/App.tsx' },
    { name: 'index.html', desc: 'Entry point — SRI tags presentes', status: 'pass', hash: 'sha384-nRsV3...w4Tx', file: 'index.html' },
    { name: 'tailwind.config', desc: 'Config Tailwind — sin modificaciones', status: 'pass', hash: 'sha256-aKjL5...y2Ms', file: 'tailwind.config.js' },
]

export default function IntegrityPage() {
    const [tab, setTab] = useState<TabType>('checks')

    const passCount = CHECKS.filter(c => c.status === 'pass').length
    const totalCount = CHECKS.length

    const sevStyles: Record<CheckStatus, { icon: React.ReactNode; color: string }> = {
        pass: { icon: <CheckCircle2 size={14} />, color: 'text-neon-green' },
        warning: { icon: <AlertTriangle size={14} />, color: 'text-yellow-400' },
        fail: { icon: <XCircle size={14} />, color: 'text-red-400' },
    }

    const tabs = [
        { id: 'checks' as const, label: 'Integrity Checks', icon: <ShieldCheck size={16} /> },
        { id: 'sri' as const, label: 'SRI / Hashes', icon: <Hash size={16} /> },
        { id: 'docs' as const, label: 'Anti-Tampering', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <ShieldCheck className="text-neon-blue" /> Integridad de Código
                </h1>
                <p className="text-gray-500 text-sm">Verificación anti-tampering con SRI y hash checks</p>
            </div>

            <FeatureNotEnabled moduleName="Integridad de Código (SRI)" />

            <Card className={`mb-6 ${passCount === totalCount ? 'bg-neon-green/5 border border-neon-green/20' : 'bg-red-500/5 border border-red-500/20'}`}>
                <div className="flex items-center gap-4">
                    {passCount === totalCount ? <ShieldCheck size={24} className="text-neon-green" /> : <AlertTriangle size={24} className="text-red-400" />}
                    <div>
                        <span className={`font-bold text-sm ${passCount === totalCount ? 'text-neon-green' : 'text-red-400'}`}>
                            {passCount === totalCount ? `✅ ${passCount}/${totalCount} archivos verificados — sin tampering` : `⚠️ Tampering detectado`}
                        </span>
                        <p className="text-gray-500 text-xs mt-0.5">Última verificación: hace 2 minutos</p>
                    </div>
                </div>
            </Card>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'checks' && (
                <div className="space-y-2">
                    {CHECKS.map(check => {
                        const s = sevStyles[check.status]
                        return (
                            <Card key={check.name} className="!p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={s.color}>{s.icon}</div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <code className="text-neon-blue text-xs font-mono">{check.name}</code>
                                                <span className="text-[10px] text-gray-600">{check.file}</span>
                                            </div>
                                            <p className="text-gray-500 text-xs mt-0.5">{check.desc}</p>
                                        </div>
                                    </div>
                                    <code className="text-[10px] text-gray-600 font-mono bg-white/5 px-2 py-1 rounded">{check.hash}</code>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}

            {tab === 'sri' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Hash size={16} className="text-neon-blue" /> Subresource Integrity (SRI)</h2>
                    <pre className="bg-black/30 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto border border-white/5">{`<!-- index.html — SRI en scripts -->
<script
  src="/assets/index-a1b2c3.js"
  integrity="sha384-oqVuAfXRKap7fdgcCY5uykM6+Rk8vLMdhHT/S0TkN5Hg2bL+8="
  crossorigin="anonymous">
</script>

<!-- Verificación en runtime -->
<script>
  const expected = 'sha384-oqVuAfXRKap7fdgcCY5...'
  const actual = await computeSRI('/assets/index-a1b2c3.js')
  if (actual !== expected) {
    // TAMPERING DETECTED → kill app
    document.body.innerHTML = ''
    throw new Error('CODE_INTEGRITY_VIOLATION')
  }
</script>`}</pre>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-3">Estrategia Anti-Tampering</h2>
                        <div className="space-y-2">
                            {[
                                { step: '1', title: 'Build-time hashing', desc: 'Vite genera hashes SHA-384 de cada chunk durante el build' },
                                { step: '2', title: 'SRI tags', desc: 'Los tags integrity se inyectan automáticamente en index.html' },
                                { step: '3', title: 'Runtime verification', desc: 'vendor_core verifica hashes al cargar — si no coinciden, abort' },
                                { step: '4', title: 'Telemetry alert', desc: 'Cualquier fallo de integridad se reporta a la Torre de Control' },
                            ].map(s => (
                                <div key={s.step} className="flex items-start gap-3 p-3 bg-black/20 rounded-lg">
                                    <span className="w-6 h-6 rounded-full bg-neon-blue/10 text-neon-blue text-xs font-bold flex items-center justify-center shrink-0">{s.step}</span>
                                    <div>
                                        <span className="text-white text-sm font-medium">{s.title}</span>
                                        <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
